import { useEffect, useRef, useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { toast } from "@volca/ui/lib/toast";
import { cn } from "@volca/ui/lib/utils";
import { useApBusinessTimeZone } from "@/accounts-payable/common";
import { downloadInvoiceExport } from "@/accounts-payable/query";
import { apToday, formatFileSize } from "@/accounts-payable/utils";
import { useApi } from "@/core/api";
import { ApiError } from "@/core/api/types";
import useAuth from "@/core/auth/use-auth";
import { ExportFields } from "./export-fields";
import { type InvoiceExportOptions, exportListFilters } from "./export-options";
import { invoiceExportFilename, saveFile } from "./invoice-export";
import type { InvoiceFilter } from "./use-invoices-tab";

/**
 * Picks which invoices to export and downloads every one that matches as a
 * CSV the API builds, so a period can be kept as a record.
 */
export const ExportInvoicesDialog = ({
  businessId,
  statusFilters,
  initialOptions,
  onClose,
}: {
  businessId: string;
  statusFilters: InvoiceFilter[];
  initialOptions: InvoiceExportOptions;
  onClose: () => void;
}) => {
  const api = useApi();
  const { user } = useAuth();
  const timeZone = useApBusinessTimeZone();
  const [options, setOptions] = useState(initialOptions);
  const [exporting, setExporting] = useState(false);
  const [received, setReceived] = useState(0);
  const [message, setMessage] = useState<string | null>(null);
  const controller = useRef<AbortController | null>(null);
  useEffect(() => () => controller.current?.abort(), []);

  const change = (changes: Partial<InvoiceExportOptions>) => {
    setOptions((current) => ({ ...current, ...changes }));
    setMessage(null);
  };

  const exportCsv = async () => {
    if (controller.current) return;
    const request = new AbortController();
    controller.current = request;
    setExporting(true);
    setReceived(0);
    setMessage(null);
    try {
      const file = await downloadInvoiceExport(
        api,
        businessId,
        options.kind,
        exportListFilters(options, statusFilters, user?.id),
        { signal: request.signal, onProgress: setReceived },
      );
      saveFile(
        file,
        invoiceExportFilename(
          options.kind,
          { field: options.dateField, days: options.days },
          apToday(timeZone),
        ),
      );
      toast.success("Invoices exported");
      onClose();
    } catch (error) {
      if (!request.signal.aborted)
        setMessage(
          error instanceof ApiError && error.status === 404
            ? "No invoices match these filters."
            : "Couldn’t export invoices. Please try again in a moment.",
        );
    } finally {
      controller.current = null;
      if (!request.signal.aborted) setExporting(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {/* Taller than a phone held sideways, so it scrolls rather than
          centring its title and Export button off-screen. */}
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Export invoices</DialogTitle>
          <DialogDescription>
            Download the invoices that match as a CSV.
          </DialogDescription>
        </DialogHeader>
        <ExportFields
          businessId={businessId}
          statusFilters={statusFilters}
          options={options}
          onChange={change}
          disabled={exporting}
        />
        {message && (
          <p role="alert" className="text-sm text-danger">
            {message}
          </p>
        )}
        {/* A long history takes a while; this says it's still coming. */}
        <p
          role="status"
          className={cn("text-sm text-subtle", { "sr-only": !exporting })}
        >
          {exporting &&
            (received > 0
              ? `Downloading… ${formatFileSize(received)}`
              : "Preparing the export…")}
        </p>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => void exportCsv()}
            loading={exporting}
            loadingLabel="Exporting invoices"
          >
            Export
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
